import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Chip, Stack, CircularProgress, TextField } from '@mui/material';
import { format } from 'date-fns';
import { fetchMedicineBatches, fetchNonMedicineBatches } from 'utils/apiHelper';
import { calculateBatchAllocation } from 'utils/batchAllocation';
import { TableRow, TableCell } from '@mui/material';


const BatchAllocationDisplay = ({ medicineId, nonMedicineId, quantity, storeId, itemName, 
    itemType, colSpan, isRowFocused, variant, salePriceType, itemData, index, setFieldValue }) => {
    const [loading, setLoading] = useState(false);
    const [allocation, setAllocation] = useState({ allocations: [], hasInsufficientStock: false });
    const [allBatches, setAllBatches] = useState([]);
    const safeQuantity = Number.isFinite(Number(quantity)) ? Number(quantity) : 0;
    const skipAutoAllocationRef = useRef(false);
    const lastContextRef = useRef({ itemId: null, storeId: null });

    const setAllocationField = useCallback((allocationResult) => {
        setFieldValue(`invoice_detail.${index}.batch_allocations`, allocationResult);
    }, [setFieldValue, index]);

    const isBatchExpired = useCallback((expiryDate) => {
        if (!expiryDate) return false;
        const parsed = new Date(expiryDate);
        if (Number.isNaN(parsed.getTime())) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        parsed.setHours(0, 0, 0, 0);
        return parsed <= today;
    }, []);

    useEffect(() => {
        const itemId = medicineId || nonMedicineId;
        const isSameContext = lastContextRef.current.itemId === itemId && lastContextRef.current.storeId === storeId;

        if (skipAutoAllocationRef.current && isSameContext) {
            skipAutoAllocationRef.current = false;
            return;
        }

        lastContextRef.current = { itemId, storeId };

        if (!itemId || !storeId || safeQuantity < 0) {
            const emptyAllocation = { allocations: [], hasInsufficientStock: false };
            setAllocation(emptyAllocation);
            setAllocationField(emptyAllocation);
            return;
        }

        const fetchBatches = async () => {
            setLoading(true);
            try {
                let batchData;
                // Fetch batches based on item type - medicine or non-medicine
                if (nonMedicineId) {
                    batchData = await fetchNonMedicineBatches(nonMedicineId, storeId);
                } else {
                    batchData = await fetchMedicineBatches(medicineId, storeId);
                }

                if (batchData.length > 0) {
                    const batchDetails = (batchData[0]?.batch_details || []).filter(
                        (batch) => !isBatchExpired(batch?.expiry_date)
                    );
                    setAllBatches(batchDetails);
                    const allocationResult = calculateBatchAllocation(batchDetails, safeQuantity || 0);
                    setAllocationField(allocationResult);
                    setAllocation(allocationResult);
                } else {
                    const emptyAllocation = { allocations: [], hasInsufficientStock: true };
                    setAllBatches([]);
                    setAllocation(emptyAllocation);
                    setAllocationField(emptyAllocation);
                }
            } catch (error) {
                console.error('Error fetching batches:', error);
                const emptyAllocation = { allocations: [], hasInsufficientStock: true };
                setAllBatches([]);
                setAllocation(emptyAllocation);
                setAllocationField(emptyAllocation);
            } finally {
                setLoading(false);
            }
        };

        fetchBatches();
    }, [medicineId, nonMedicineId, safeQuantity, storeId, isBatchExpired, setAllocationField]);

    const itemId = medicineId || nonMedicineId;
    if (!itemId || !itemName) {
        return null;
    }

    const availableStock = allBatches.reduce((sum, b) => sum + Number(b.current_stock || 0), 0);

    const handleManualAllocationChange = (batchNumber, rawValue) => {
        const qty = Math.max(0, Number(rawValue) || 0);
        const detail = allBatches.find(b => b.batch_number === batchNumber);
        const stock = Number(detail?.current_stock || 0);
        const capped = Math.min(qty, stock);
        const current = allocation.allocations || [];
        const next = current.some(a => a.batchNumber === batchNumber)
            ? current.map(a => a.batchNumber === batchNumber ? { ...a, quantity: capped } : a)
            : [...current, { batchNumber, quantity: capped, expiryDate: detail?.expiry_date, sellingPrice: detail?.selling_price, unitCost: detail?.buying_price }];
        const filtered = next.filter(a => a.quantity > 0);
        const totalSelected = filtered.reduce((sum, a) => sum + Number(a.quantity || 0), 0);
        const payload = {
            allocations: filtered,
            hasInsufficientStock: totalSelected > availableStock,
            availableStock,
            requestedQuantity: totalSelected
        };
        setAllocation(payload);
        setAllocationField(payload);

        // Keep visible quantity in sync with allocated batches
        if (itemData?.qty !== totalSelected) {
            skipAutoAllocationRef.current = true;
            setFieldValue(`invoice_detail.${index}.qty`, totalSelected);

            if (itemData?.packaging_size) {
                const strips = Math.floor(totalSelected / itemData.packaging_size);
                const pcs = totalSelected % itemData.packaging_size;
                setFieldValue(`invoice_detail.${index}.strips`, strips);
                setFieldValue(`invoice_detail.${index}.pcs`, pcs);
            }
        }
    };

    if (loading) {
        return (
            <TableRow>
                <TableCell colSpan={colSpan}>
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="text.secondary">
                            Loading batch information...
                        </Typography>
                    </Box>
                </TableCell>
            </TableRow>
        );
    }

    if (allocation.hasInsufficientStock) {
        return (
            <TableRow>
                <TableCell colSpan={colSpan}>
                    <Box sx={{ mt: 1, p: 1, bgcolor: 'error.main', borderRadius: 1 }}>
                        <Typography variant="caption" color="white" sx={{ fontWeight: 500 }}>
                            ⚠️ Insufficient Stock
                        </Typography>
                        {allocation.availableStock !== undefined && (
                            <Typography variant="caption" color="white" display="block">
                                Available: {allocation.availableStock} | Requested: {allocation.requestedQuantity}
                            </Typography>
                        )}
                    </Box>
                </TableCell>
            </TableRow>
        );
    }

    const canEditAllocation = isRowFocused && (variant === 'sale' || variant === 'pharmacy');

    if (allocation.allocations.length === 0 && !canEditAllocation) {
        return null;
    }

    if (canEditAllocation) {
        return (
            <TableRow>
                <TableCell colSpan={colSpan}>
                    <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                        <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="flex-start">
                            <Stack spacing={1} sx={{ minWidth: 260, flex: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    Batch Allocation
                                </Typography>
                                <Stack direction="row" flexWrap="wrap" rowGap={1} columnGap={1}>
                                    {allBatches.map(batch => {
                                        const existing = allocation.allocations.find(a => a.batchNumber === batch.batch_number);
                                        return (
                                            <Box key={batch.batch_number} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.5, bgcolor: 'white' }}>
                                                <Chip label={batch.batch_number} size="small" />
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={existing?.quantity || ''}
                                                    onChange={(e) => handleManualAllocationChange(batch.batch_number, e.target.value)}
                                                    inputProps={{ min: 0, max: batch.current_stock }}
                                                    sx={{ width: 90 }}
                                                />
                                                <Typography variant="caption" color="text.secondary">Stock {batch.current_stock}</Typography>
                                                <Typography variant="caption" color="text.secondary">Exp {batch.expiry_date}</Typography>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            </Stack>
                            <Stack spacing={1} sx={{ minWidth: 200 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, textAlign: 'right' }}>
                                    Available Batches
                                </Typography>
                                <Stack direction="row" flexWrap="wrap" justifyContent="flex-end" rowGap={0.5} columnGap={0.5}>
                                    {allBatches.map((batch, index) => (
                                        <Chip
                                            key={index}
                                            label={`${batch.batch_number}: ${batch.current_stock}`}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            sx={{ fontSize: '0.7rem', height: 20 }}
                                        />
                                    ))}
                                </Stack>
                            </Stack>
                        </Stack>
                    </Box>
                </TableCell>
            </TableRow>
        );
    }

    return (
        <TableRow>
            <TableCell colSpan={colSpan}>
                <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                    <Stack justifyContent="space-between" spacing={1} direction="row" flexWrap="wrap">
                        <Stack direction="column" sx={{ width: '50%'}}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>
                                {itemType === 'non-medical' ? 'Stock Allocation:' : 'Batch Allocation:'}
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" width="100%">
                                {allocation.allocations.map((alloc, index) => (
                                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, paddingBottom: 0.5, paddingRight: 0.5 }}>
                                        <Chip
                                            label={`${alloc.quantity} from ${alloc.batchNumber}`}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            sx={{ fontSize: '0.7rem', height: 20 }}
                                        />
                                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                            Exp: {alloc.expiryDate ? format(new Date(alloc.expiryDate), 'dd/MM/yyyy') : 'N/A'}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Stack>
                        <Stack direction="column" sx={{ width: '45%'}}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5, display: 'block', textAlign: 'right', width: '100%' }}>
                                {itemType === 'non-medical' ? ' Available Stocks:' : 'Available Batches:'}
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" width="100%" justifyContent="flex-end">
                                {allBatches.map((batch, index) =>
                                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', paddingBottom: 0.5, paddingLeft: 0.5 }}>
                                        <Chip
                                            key={index}
                                            label={`${batch.batch_number}: ${batch.current_stock}`}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            sx={{ fontSize: '0.7rem', height: 20 }}
                                        />
                                    </Box>
                                )}
                            </Stack>
                        </Stack>
                    </Stack>
                </Box>
            </TableCell>
        </TableRow>
    );
};

export default BatchAllocationDisplay; 